import React, { useContext, useEffect, useState } from "react";
import { Wrapper, FormBody, Section } from "./style";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import FormHeader from "../../FormHeader";
import QuestionHeader from "../../QuestionHeader";
import { useConfiguration } from "../../../contexts/Configuration";
import RowOptions from "../../RowOptions";
import QuestionFormCheckbox from "../../QuestionFormCheckbox";
import RichTextField from "../../RichTextField";
import RequiredCheckbox from "../../RequiredCheckbox";
import AnswerTypesField from "../../AnswerTypesField";
import { useAppDispatch, useAppSelector } from "../../../../../../../app/hooks";
import { NetworkErrorAlertContext } from "../../../../../../../context/NetworkErrorAlert";
import { SuccessAlertContext } from "../../../../../../../context/SuccessAlertContext";
import {
  setNewQuestion,
  setSelectedQuestion,
  setUpdatedQuestion,
} from "../../../../../../../pages/Design/designSlice";
import pick from "lodash/pick";
import isEqual from "lodash/isEqual";
import { useParams } from "react-router-dom";
import {
  useAddQuestionMutation,
  useUpdateQuestionMutation,
} from "../../../../../../../app/apiSlice";
import ErrorMessage from "../../../../../../common/ErrorMessage";

type Inputs = {
  text: string;
};
const CheckboxForm = ({ onCancel }: { onCancel: () => void }) => {
  const dispatch = useAppDispatch();
  const { surveyId } = useParams();
  const [hasOptionsError, setHasOptionsError] = useState(false);
  const { selectedQuestion } = useAppSelector((state) => state.design);
  const { onShowAlert: onShowErrorAlert } = useContext(
    NetworkErrorAlertContext
  );
  const { onShowAlert: onSuccessShowAlert } = useContext(SuccessAlertContext);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({ defaultValues: { text: selectedQuestion.text } });
  const [addQuestion, { isLoading: isLoadingAddQues }] =
    useAddQuestionMutation();
  const [editQuestion, { isLoading: isLoadingUpdateQues }] =
    useUpdateQuestionMutation();
  const { state: configState, dispatch: localDispatch } = useConfiguration();
  const {
    rowOptions,
    hasRandomizedChoices,
    isRequired,
    requiredMessage,
    checkboxOptions,
  } = configState;

  useEffect(() => {
    if (selectedQuestion.text) {
      setValue("text", selectedQuestion.text);
    }
  }, [selectedQuestion]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const rowOptionsNotEmpty = rowOptions.filter(
      (opt) => opt.value?.length !== 0
    );
    if (rowOptionsNotEmpty.length < 2) {
      setHasOptionsError(true);
    } else {
      setHasOptionsError(false);
      try {
        const mcValues = {
          type: "Ch",
          text: data.text,
          rowOptions,
          isRequired,
          requiredMessage: undefined,
          hasLogic: false,
          hasRandomizedChoices,
          choicesNumber: checkboxOptions.choicesNumber,
          choicesType: checkboxOptions.choicesType,
        };
        if (isRequired) {
          mcValues.requiredMessage = requiredMessage;
        }
        if (!selectedQuestion?.questionId) {
          const result = await addQuestion({
            surveyId,
            questionDetail: mcValues,
          }).unwrap();
          await dispatch(setNewQuestion(result?.question));
          await dispatch(setSelectedQuestion(null));
          onSuccessShowAlert("New question added");
        } else {
          const { questionId } = selectedQuestion;
          const mcQuesInitialObj = pick(selectedQuestion, [
            "type",
            "text",
            "rowOptions",
            "isRequired",
            "hasLogic",
            "hasRandomizedChoices",
            "requiredMessage",
            "choicesType",
            "choicesNumber",
            // "maxSelectedCheckbox",
          ]);
          const hasQuestionNotChanged = isEqual(mcQuesInitialObj, mcValues);
          if (!hasQuestionNotChanged) {
            await editQuestion({
              surveyId,
              questionId,
              questionDetail: mcValues,
            }).unwrap();
            await dispatch(
              setUpdatedQuestion({
                questionId,
                updatedQues: { ...mcValues, questionId },
              })
            );
          }
          onSuccessShowAlert("Question updated");
        }
      } catch (err) {
        onShowErrorAlert(err, "The question couldn't be saved");
      }
    }
  };

  useEffect(() => {
    if (selectedQuestion.text) {
      localDispatch({
        type: "UPDATE_ROW_OPTIONS",
        payload: selectedQuestion.rowOptions,
      });
      localDispatch({
        type: "UPDATE_IS_REQUIRED",
        payload: selectedQuestion.isRequired,
      });
      setValue("text", selectedQuestion.text);
    }
  }, [selectedQuestion]);

  const [randomize, setRandomize] = useState(hasRandomizedChoices || false);

  const onToggleRequired = (e: any) => {
    setRandomize(e.target.checked);
  };
  return (
    <Wrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormHeader onCancel={onCancel} />
        <FormBody>
          <QuestionHeader questionType="Ch" />
          <Controller
            name="text"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <RichTextField
                hasError={errors.text?.type === "required"}
                initialValue={selectedQuestion.text}
                field={field}
              />
            )}
          />
          <Section>
            <AnswerTypesField />
            <RowOptions rowList={rowOptions} />
            {hasOptionsError && (
              <ErrorMessage message="Minimum of two options" />
            )}
          </Section>

          <Section>
            <RequiredCheckbox questionType="Ch" />
            <div>
              <QuestionFormCheckbox
                value={randomize}
                onChange={onToggleRequired}
              >
                Randomize options
              </QuestionFormCheckbox>
            </div>
          </Section>
        </FormBody>
      </form>
    </Wrapper>
  );
};

export default CheckboxForm;
